import { gql, TypedDocumentNode } from "@apollo/client";
import { CCMS_SUSPENSION_FRAGMENT } from "@/fragments";
import type { CRMNote } from "@/types";
import type { OrderStage, OrderPaymentStatus } from "@/pages/billing/types";

export interface MergePurgeOrder {
  id: string;
  status: OrderStage;
  paymentStatus: OrderPaymentStatus;
  balance: number;
  totalPaid: number;
  dueDate?: string;
  description: string;
}

export interface MergePurgeOrdersResponse {
  commerceOrders: {
    nodes: MergePurgeOrder[];
  };
}

export interface MergePurgeNotesResponse {
  personNotes: {
    nodes: CRMNote[];
  };
}

export const MERGE_PURGE_NOTES_QUERY: TypedDocumentNode<MergePurgeNotesResponse> = gql`
  query MergePurgeNotes($pgaId: ID!) {
    personNotes(pgaId: $pgaId, orderBy: { field: UPDATED_AT, direction: DESC }) {
      nodes {
        id
        title
        displayTitle
        content
        updatedAt
        createdAt
      }
    }
  }
`;

export const MERGE_PURGE_PERSON_QUERY = gql`
  query MergePurgePerson($pgaId: ID!) {
    person(pgaId: $pgaId) {
      id
      pgaId
      firstName
      middleName
      lastName
      displayName
      informalName
      preferredLastName
      suffixName
      profilePhoto
      email
      dob {
        day
        month
        year
      }
      ssn {
        last4
      }
      primarySection {
        id
        name
      }
      latestProgram {
        id
        status
      }
      phoneNumbers {
        id
        phoneNumber
        phoneType
      }
      contactAddressId
      addresses {
        id
        address1
        address2
        city
        state
        postalCode
        addressType
      }
      employments {
        id
        isPrimary
        title
        formattedTitle
        startedOn
        endedOn
        jobClassification
        facility {
          id
          name
        }
      }
      bands {
        id
        createdReason
        startedOn
        expiresOn
        programType {
          id
          name
        }
        classification {
          id
          name
        }
      }
      ccmsSuspensions {
        ...CcmsSuspensionFragment
      }
      legacyDocuments {
        createdAt
        fileName
        description
        documentType
      }
      legacyBioPhoto {
        createdAt
      }
      auth0LastLogin
      auth0LoginCount
    }
  }
  ${CCMS_SUSPENSION_FRAGMENT}
`;

export interface PeopleProcessMergeResponse {
  peopleProcessMerge: {
    success: boolean;
    message: string;
  };
}

export const PEOPLE_PROCESS_MERGE_MUTATION: TypedDocumentNode<PeopleProcessMergeResponse> = gql`
  mutation PeopleProcessMerge($input: PeopleProcessMergeInput!) {
    peopleProcessMerge(input: $input) {
      success
      message
    }
  }
`;

export interface PeopleProcessPurgeResponse {
  peopleProcessPurge: {
    success: boolean;
    message: string;
  };
}

export const PEOPLE_PROCESS_PURGE_MUTATION: TypedDocumentNode<PeopleProcessPurgeResponse> = gql`
  mutation PeopleProcessPurge($purgeOnly: Boolean = false, $sourcePersonId: ID!) {
    peopleProcessPurge(purgeOnly: $purgeOnly, sourcePersonId: $sourcePersonId) {
      success
      message
    }
  }
`;

export interface PeopleProcessAuth0Response {
  peopleProcessAuth0: {
    success: boolean;
    message: string;
  };
}

export const PEOPLE_PROCESS_AUTH0_MUTATION: TypedDocumentNode<PeopleProcessAuth0Response> = gql`
  mutation PeopleProcessAuth0($sourcePersonId: ID!, $targetPersonId: ID!) {
    peopleProcessAuth0(sourcePersonId: $sourcePersonId, targetPersonId: $targetPersonId) {
      success
      message
    }
  }
`;

export const MERGE_PURGE_ORDERS_QUERY: TypedDocumentNode<MergePurgeOrdersResponse> = gql`
  query MergePurgeOrders($query: String) {
    commerceOrders(filters: { search: $query }) {
      nodes {
        id
        status
        paymentStatus
        balance
        totalPaid
        dueDate
        description
      }
    }
  }
`;
